import React, {FC, useState, useEffect} from 'react'
import smoothScrollTo from 'smoothscroll'
import {reachGoal, trackEvent} from 'utils/metrics'
import {useViewport} from '@rambler-id/react'
import {TWO_FACTOR_AUTHN} from 'utils/urls'
import {
  Button,
  H2,
  ChevronUpCompactIcon,
  ChevronDownCompactIcon
} from 'rambler-ui'
import {Link} from 'react-router-dom'
import {TfaIcon} from './icons/tfa-icon'
import {WebAuthnIcon} from './icons/webauthn-icon'
import {DataIcon} from './icons/data-icon'
import {SocialIcon} from './icons/social-icon'
import styles from './styles.css'

export interface SuggestionsProps {
  webAuthnAvailable: boolean
  webAuthnKeys: {
    id: string
    properties: {title: string; browser: string}
  }[]
  suggestTfa: boolean
  profile: {
    firstname?: string
    lastname?: string
    birthdate: number
    gender?: 'm' | 'f' | 'u'
    geoid?: number
  }
  socialProfiles: {
    displayName: string
    id: string
    oauthAvatar: string
    provider: string
  }[]
}

const handleClick = (section: string, key: string): void => {
  const element = document.querySelector(`[data-profile-section=${section}]`)

  smoothScrollTo(element)
  track(key)
}

const track = (key: string): void => {
  trackEvent('suggestions', {[key]: true})
  reachGoal(`suggestions_${key}`)
}

const Suggestions: FC<SuggestionsProps> = ({
  webAuthnAvailable,
  webAuthnKeys,
  suggestTfa,
  profile: {birthdate, gender, geoid}
}) => {
  const [expanded, setExpanded] = useState<boolean>(true)
  const {isMobile} = useViewport()

  const suggestWebAuthn = webAuthnAvailable && webAuthnKeys.length === 0
  const suggestBirthdate = birthdate == null || birthdate === -4000000001
  const suggestGender = gender == null || gender === 'u'
  const suggestGeo = geoid == null || geoid === 0
  // const suggestSocial = socialProfiles.length === 0
  const suggestSocial = false

  useEffect(() => {
    if (suggestTfa) track('tfa_show')
    if (suggestWebAuthn) track('web_authn_show')
    if (suggestBirthdate) track('birthday_show')
    if (suggestGender) track('gender_show')
    if (suggestGeo) track('geo_show')
    if (suggestSocial) track('social_show')
  }, [
    suggestBirthdate,
    suggestGender,
    suggestGeo,
    suggestSocial,
    suggestTfa,
    suggestWebAuthn
  ])

  if (
    !suggestTfa &&
    !suggestWebAuthn &&
    !suggestBirthdate &&
    !suggestGender &&
    !suggestGeo &&
    !suggestSocial
  ) {
    return null
  }

  const nbsp = isMobile ? ' ' : <>&nbsp;</>

  return (
    <section className={`${styles.banner} ${expanded ? styles.expanded : ''}`}>
      <Button
        onClick={() => setExpanded(!expanded)}
        className={styles.button}
        type="flat"
        width={isMobile ? 20 : 15}>
        {expanded ? (
          <ChevronUpCompactIcon size={isMobile ? 20 : 15} />
        ) : (
          <ChevronDownCompactIcon size={isMobile ? 20 : 15} />
        )}
      </Button>
      {!expanded ? (
        <H2>Защитите свой профиль, добавив недостающие данные</H2>
      ) : (
        <>
          <H2>
            Вы можете усилить защиту вашего профиля и упростить восстановление
            доступа, если забудете пароль. Добавьте информацию о себе и
            подключите другие способы входа.
          </H2>
          {suggestTfa && (
            <div className={styles.item}>
              {isMobile ? <div className={styles.dot} /> : <TfaIcon />}
              <p>
                Рекомендуем подключить{' '}
                <Link to={TWO_FACTOR_AUTHN} onClick={() => track('tfa_click')}>
                  двухфакторную аутентификацию
                </Link>
              </p>
            </div>
          )}
          {suggestWebAuthn && (
            <div className={styles.item}>
              {isMobile ? <div className={styles.dot} /> : <WebAuthnIcon />}
              <p>
                Настройте{' '}
                <span
                  onClick={() => handleClick('webauthn', 'web_authn_click')}
                  role="none">
                  вход по отпечатку пальца или по Face ID
                </span>
              </p>
            </div>
          )}
          {(suggestBirthdate || suggestGender || suggestGeo) && (
            <div className={styles.item}>
              {isMobile ? <div className={styles.dot} /> : <DataIcon />}
              <p>
                Добавьте информацию о себе:{' '}
                {suggestBirthdate && (
                  <>
                    <span
                      onClick={() => handleClick('general', 'birthday_click')}
                      role="none">
                      дату рождения
                    </span>
                    {suggestGender && suggestGeo ? (
                      ', '
                    ) : suggestGender || suggestGeo ? (
                      <> и{nbsp}</>
                    ) : (
                      ''
                    )}
                  </>
                )}
                {suggestGender && (
                  <>
                    <span
                      onClick={() => handleClick('general', 'gender_click')}
                      role="none">
                      пол
                    </span>
                    {suggestGeo && <> и{nbsp}</>}
                  </>
                )}
                {suggestGeo && (
                  <span
                    onClick={() => handleClick('general', 'geo_click')}
                    role="none">
                    место{nbsp}проживания
                  </span>
                )}
              </p>
            </div>
          )}
          {suggestSocial && (
            <div className={styles.item}>
              {isMobile ? <div className={styles.dot} /> : <SocialIcon />}
              <p>
                <span
                  onClick={() => handleClick('social', 'social_click')}
                  role="none">
                  Привяжите социальные сети
                </span>{' '}
                для быстрого входа в свой профиль
              </p>
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default Suggestions
